import {
    useAppSidePanelContextProvider
} from "../../../../../shared/contexts/app-side-panel-context-provider/components/AppSidePanelContextProvider";
import ReactEcharts from "echarts-for-react";
import { ParameterType } from "../../../../options-table/types/ParameterType";
import { Menu, MenuItem, Stack } from "@mui/material";
import { useState } from "react";
import { ConfigureParametersChartDialog } from "../../../../portfolio-info/components/ConfigureParametersChartDialog";
import { chartDataSourceService } from "./services/ChartDataSourceService";
import { chartDataSeriesService } from "./services/ChartDataSeriesService";
import { ProfileType } from "../../../../options-table/types/ProfileType";
import { chartTooltipService } from "./services/ChartTooltipService";
import { ParameterTypeSelector } from "../../ParameterTypeSelector";

export function PortfolioGreekProfile() {
    const {
        marketDataDto,
        portfolioProfile,
        selectedPortfolio,
        comparedPortfolioProfile,
    } = useAppSidePanelContextProvider();

    const [isConfigureChartDialogOpen, setIsConfigureChartDialogOpen] = useState<boolean>(false);
    const [isChartTooltipVisible, setIsChartTooltipVisible] = useState<boolean>(true);

    const [contextMenu, setContextMenu] = useState<{
        mouseX: number;
        mouseY: number;
    } | null>(null);
    
    if (selectedPortfolio === null || selectedPortfolio.portfolioSettings === null) {
        return null;
    }

    const settings = selectedPortfolio.portfolioSettings;

    const isProfileTypeSelected = (
        profileType: ProfileType,
        parameterType: ParameterType,
        isCompared: boolean
    ): boolean =>
        settings.selectedOptionParameterType === parameterType &&
        isCompared
            ? settings.selectedProfileTypes.indexOf(profileType) > -1 &&
                selectedPortfolio!.portfolioSettings!.useComparedPortfolio
            : settings.selectedProfileTypes.indexOf(profileType) > -1;

    const handleContextMenu = (event: React.MouseEvent) => {
        event.preventDefault();
        setContextMenu(
            contextMenu === null
                ? {
                    mouseX: event.clientX + 2,
                    mouseY: event.clientY - 6,
                }
                : null,
        );
    };

    const handleClose = (event: any) => {
        setContextMenu(null);
        event.stopPropagation();
    };

    const handleOpeningConfigureChartsDialog = (event: any) => {
        setContextMenu(null);
        event.stopPropagation();
        setIsConfigureChartDialogOpen(true);
    };
    
    function handleChangeChartTooltipVisibility() {
        setIsChartTooltipVisible(!isChartTooltipVisible);
        setContextMenu(null);
    }

    const dataSource: number[][] = chartDataSourceService.getDataSource(
        portfolioProfile, selectedPortfolio.portfolioSettings, comparedPortfolioProfile);
    const series: any[] = chartDataSeriesService.getSelectedChartSeries(
        marketDataDto!, portfolioProfile, comparedPortfolioProfile, selectedPortfolio.portfolioSettings);
    
    return (
        <Stack
            sx={{
                flex: '1',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}
            onContextMenu={handleContextMenu}
        >
            <ConfigureParametersChartDialog
                isOpen={isConfigureChartDialogOpen}
                setIsOpen={setIsConfigureChartDialogOpen}
            />
            <Menu
                open={contextMenu !== null}
                onClose={handleClose}
                anchorReference="anchorPosition"
                anchorPosition={
                    contextMenu !== null
                        ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                        : undefined
                }
            >
                <MenuItem onClick={handleOpeningConfigureChartsDialog}>Configure chart...</MenuItem>
                <MenuItem onClick={handleChangeChartTooltipVisibility}>
                    {
                        isChartTooltipVisible
                            ? "Hide tooltip"
                            : "Show tooltip"
                    }
                </MenuItem>
            </Menu>
            <Stack direction="row" spacing={2}>
                <ParameterTypeSelector />
            </Stack>
            <div
                style={{
                    marginTop: '7px',
                    marginBottom: '7px',
                    borderBottom: 'solid 1px #bdbcbc',
                }}
            />
            <ReactEcharts
                theme="light"
                style={{
                    flex: '1',
                }}
                option={{
                    grid: {
                        width: 'auto',
                        height: 'auto',
                        left: 55,
                        right: 0,
                        top: 20,
                        bottom: 25,
                    },
                    legend: {
                        show: false,
                            selected: {
                                'Current delta': isProfileTypeSelected(ProfileType.Current, ParameterType.Delta, false),
                                'Compared current delta': isProfileTypeSelected(ProfileType.Current, ParameterType.Delta, true),
                                'Custom delta': isProfileTypeSelected(ProfileType.Custom, ParameterType.Delta, false),
                                'Compared custom delta': isProfileTypeSelected(ProfileType.Custom, ParameterType.Delta, true),
                                'Expiration delta': isProfileTypeSelected(ProfileType.Expiration, ParameterType.Delta, false),
                                'Compared expiration delta': isProfileTypeSelected(ProfileType.Expiration, ParameterType.Delta, true),
                                
                                'Current gamma': isProfileTypeSelected(ProfileType.Current, ParameterType.Gamma, false),
                                'Compared current gamma': isProfileTypeSelected(ProfileType.Current, ParameterType.Gamma, true),
                                'Custom gamma': isProfileTypeSelected(ProfileType.Custom, ParameterType.Gamma, false),
                                'Compared custom gamma': isProfileTypeSelected(ProfileType.Custom, ParameterType.Gamma, true),
                                'Expiration gamma': isProfileTypeSelected(ProfileType.Expiration, ParameterType.Gamma, false),
                                'Compared expiration gamma': isProfileTypeSelected(ProfileType.Expiration, ParameterType.Gamma, true),

                                'Current vega': isProfileTypeSelected(ProfileType.Current, ParameterType.Vega, false),
                                'Compared current vega': isProfileTypeSelected(ProfileType.Current, ParameterType.Vega, true),
                                'Custom vega': isProfileTypeSelected(ProfileType.Custom, ParameterType.Vega, false),
                                'Compared custom vega': isProfileTypeSelected(ProfileType.Custom, ParameterType.Vega, true),
                                'Expiration vega': isProfileTypeSelected(ProfileType.Expiration, ParameterType.Vega, false),
                                'Compared expiration vega': isProfileTypeSelected(ProfileType.Expiration, ParameterType.Vega, true),

                                'Current theta': isProfileTypeSelected(ProfileType.Current, ParameterType.Theta, false),
                                'Compared current theta': isProfileTypeSelected(ProfileType.Current, ParameterType.Theta, true),
                                'Custom theta': isProfileTypeSelected(ProfileType.Custom, ParameterType.Theta, false),
                                'Compared custom theta': isProfileTypeSelected(ProfileType.Custom, ParameterType.Theta, true),
                                'Expiration theta': isProfileTypeSelected(ProfileType.Expiration, ParameterType.Theta, false),
                                'Compared expiration theta': isProfileTypeSelected(ProfileType.Expiration, ParameterType.Theta, true),

                                'Current rho': isProfileTypeSelected(ProfileType.Current, ParameterType.Rho, false),
                                'Compared current rho': isProfileTypeSelected(ProfileType.Current, ParameterType.Rho, true),
                                'Custom rho': isProfileTypeSelected(ProfileType.Custom, ParameterType.Rho, false),
                                'Compared custom rho': isProfileTypeSelected(ProfileType.Custom, ParameterType.Rho, true),
                                'Expiration rho': isProfileTypeSelected(ProfileType.Expiration, ParameterType.Rho, false),
                                'Compared expiration rho': isProfileTypeSelected(ProfileType.Expiration, ParameterType.Rho, true),
                        },
                        top: 5,
                    },
                    tooltip: {
                        appendToBody: true,
                        trigger: 'axis',
                        showContent: isChartTooltipVisible,
                        position: chartTooltipService.getTooltipPositionForParametersProfileChart(
                            selectedPortfolio!.portfolioSettings!, false),
                    },
                    // dataZoom: [
                    //     {
                    //         type: 'slider',
                    //         show: true,
                    //         xAxisIndex: [0],
                    //     },
                    //     {
                    //         type: 'slider',
                    //         show: true,
                    //         yAxisIndex: [0],
                    //         left: '91%',
                    //     },
                    //     {
                    //         type: 'inside',
                    //         xAxisIndex: [0],
                    //         zoomOnMouseWheel: false,
                    //     },
                    //     {
                    //         type: 'inside',
                    //         yAxisIndex: [0],
                    //         zoomOnMouseWheel: false,
                    //     }
                    // ],
                    xAxis: {
                        type: 'category',
                    },
                    yAxis: [
                        {
                            gridIndex: 0,
                            position: 'left',
                            axisTick: {
                                show: true,
                                lineStyle: {
                                    color: 'rgb(200,200,200)'
                                },
                            },
                            splitLine: {
                                show: true,
                                lineStyle: {
                                    color: 'rgb(200,200,200)'
                                },
                            },
                            minorSplitLine: {
                                show: true,
                                splitNumber: 5,
                                lineStyle: {
                                    color: 'rgb(205,205,205)',
                                    type: 'dotted'
                                }
                            }
                        }
                    ],
                    dataZoom: [
                        {
                            show: true,
                            type: 'inside',
                            filterMode: 'none',
                            xAxisIndex: [0],
                            startValue: 'min',
                            endValue: 'max'
                        },
                        {
                            show: true,
                            type: 'inside',
                            filterMode: 'none',
                            yAxisIndex: [0],
                            startValue: 'min',
                            endValue: 'max'
                        }
                    ],
                    dataset: {
                        source: dataSource,
                    },
                    series: series,
                }}
            />
        </Stack>
    );
}